import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { apiFetch } from '@/lib/api'

// Référentiel National Qualité — 7 indicateurs, 32 critères
const RNQ = [
  {
    id: 1,
    label: "Conditions d'information du public",
    criteria: [
      { id: '1.1', label: 'Diffusion d\'informations sur les prestations (objectifs, programme, modalités, tarifs)' },
      { id: '1.2', label: 'Accessibilité des informations aux publics en situation de handicap' },
      { id: '1.3', label: 'Mise à jour régulière des informations diffusées' },
      { id: '1.4', label: 'Référencement sur le site Carif-Oref et/ou Mon compte formation' },
    ],
  },
  {
    id: 2,
    label: 'Identification précise des objectifs',
    criteria: [
      { id: '2.1', label: 'Identification des objectifs de la prestation en lien avec les besoins du bénéficiaire' },
      { id: '2.2', label: 'Positionnement pédagogique initial systématique' },
      { id: '2.3', label: 'Adaptation des objectifs selon les résultats du positionnement' },
      { id: '2.4', label: 'Information du bénéficiaire sur les résultats attendus de la prestation' },
      { id: '2.5', label: 'Établissement d\'une convention ou contrat précisant les objectifs et modalités' },
      { id: '2.6', label: 'Définition et mise en œuvre des actions de suivi des résultats' },
    ],
  },
  {
    id: 3,
    label: 'Adaptation aux publics bénéficiaires',
    criteria: [
      { id: '3.1', label: 'Adaptation des méthodes et outils pédagogiques aux profils des bénéficiaires' },
      { id: '3.2', label: 'Adaptation aux bénéficiaires en situation de handicap (accessibilité)' },
      { id: '3.3', label: 'Suivi de l\'exécution de la prestation et des résultats en cours de réalisation' },
      { id: '3.4', label: 'Évaluation des acquis en fin de prestation' },
    ],
  },
  {
    id: 4,
    label: 'Adéquation des moyens pédagogiques',
    criteria: [
      { id: '4.1', label: 'Moyens humains : qualifications et compétences des formateurs' },
      { id: '4.2', label: 'Moyens techniques : équipements, locaux, outils numériques adaptés' },
      { id: '4.3', label: 'Moyens pédagogiques : supports, ressources documentaires' },
      { id: '4.4', label: 'Gestion des équipes pédagogiques et coordination' },
      { id: '4.5', label: 'Recours à la sous-traitance : conditions et suivi' },
    ],
  },
  {
    id: 5,
    label: 'Qualification et développement des compétences des personnels',
    criteria: [
      { id: '5.1', label: 'CV et titres des intervenants en lien avec les prestations assurées' },
      { id: '5.2', label: 'Développement des connaissances et compétences des personnels (formation continue)' },
      { id: '5.3', label: 'Intégration des évolutions du secteur de la formation professionnelle' },
    ],
  },
  {
    id: 6,
    label: 'Inscription dans l\'environnement professionnel',
    criteria: [
      { id: '6.1', label: 'Veille sur les évolutions des compétences et métiers dans les secteurs couverts' },
      { id: '6.2', label: 'Veille sur les évolutions des modalités de formation' },
      { id: '6.3', label: 'Mise en œuvre d\'activités dans un réseau professionnel (partenariats, branches)' },
      { id: '6.4', label: 'Contribution à des productions collectives du secteur (outils, référentiels)' },
      { id: '6.5', label: 'Collaboration avec les acteurs institutionnels du secteur de la formation' },
    ],
  },
  {
    id: 7,
    label: 'Recueil des appréciations et réclamations',
    criteria: [
      { id: '7.1', label: 'Recueil des appréciations à chaud des bénéficiaires (questionnaire de satisfaction)' },
      { id: '7.2', label: 'Recueil des appréciations à froid et mesure de l\'impact de la formation' },
      { id: '7.3', label: 'Recueil des appréciations des financeurs et prescripteurs' },
      { id: '7.4', label: 'Prise en compte des appréciations pour améliorer les prestations' },
      { id: '7.5', label: 'Traitement des réclamations : procédure définie et documentée' },
    ],
  },
]

interface QualiopiItem {
  criterion: string
  isDone: boolean
  evidence: string | null
}

export default function QualiopiPage() {
  const [items, setItems] = useState<Record<string, QualiopiItem>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState<string | null>(null)
  const [openEvidence, setOpenEvidence] = useState<string | null>(null)
  const [evidenceText, setEvidenceText] = useState('')

  async function load() {
    try {
      const data = await apiFetch<QualiopiItem[]>('/training/qualiopi')
      const map: Record<string, QualiopiItem> = {}
      for (const item of data) map[item.criterion] = item
      setItems(map)
    } catch { /* silencieux */ }
    finally { setLoading(false) }
  }

  useEffect(() => { load() }, [])

  async function toggle(indicator: number, criterion: string) {
    const current = items[criterion]
    const isDone = !(current?.isDone ?? false)
    setSaving(criterion)
    try {
      const updated = await apiFetch<QualiopiItem>('/training/qualiopi', {
        method: 'POST',
        body: JSON.stringify({ indicator, criterion, isDone, evidence: current?.evidence ?? undefined }),
      })
      setItems((prev) => ({ ...prev, [criterion]: updated }))
    } finally {
      setSaving(null)
    }
  }

  async function saveEvidence() {
    if (!openEvidence) return
    const current = items[openEvidence]
    setSaving(openEvidence)
    const parts = openEvidence.split('.')
    const indicator = parseInt(parts[0])
    try {
      const updated = await apiFetch<QualiopiItem>('/training/qualiopi', {
        method: 'POST',
        body: JSON.stringify({
          indicator,
          criterion: openEvidence,
          isDone: current?.isDone ?? false,
          evidence: evidenceText || undefined,
        }),
      })
      setItems((prev) => ({ ...prev, [openEvidence]: updated }))
      setOpenEvidence(null)
    } finally {
      setSaving(null)
    }
  }

  const totalCriteria = RNQ.reduce((s, ind) => s + ind.criteria.length, 0)
  const doneCriteria = Object.values(items).filter((i) => i.isDone).length
  const percent = Math.round((doneCriteria / totalCriteria) * 100)
  const isQualiopi = percent === 100

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-end justify-between">
        <div>
          <Link to="/centre-de-formation" className="text-text-muted hover:text-text-secondary text-sm flex items-center gap-1 mb-2">← Centre de formation</Link>
          <h1 className="text-3xl font-bold text-text-primary">Conformité Qualiopi</h1>
          <p className="text-text-secondary text-sm mt-1">Référentiel National Qualité — 7 indicateurs, 32 critères</p>
        </div>
      </div>

      {/* Score global */}
      <div className={`glass-card p-5 border ${isQualiopi ? 'border-success/30 bg-success/5' : 'border-accent/20'}`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{isQualiopi ? '🏆' : '🏅'}</span>
            <div>
              <p className={`font-bold text-lg ${isQualiopi ? 'text-success' : 'text-text-primary'}`}>
                {isQualiopi ? 'Référentiel complet !' : `${percent}% des critères couverts`}
              </p>
              <p className="text-text-muted text-xs">{doneCriteria}/{totalCriteria} critères validés</p>
            </div>
          </div>
          <div className="text-right">
            <p className={`text-3xl font-bold ${isQualiopi ? 'text-success' : 'text-accent'}`}>{percent}%</p>
          </div>
        </div>
        <div className="w-full h-3 rounded-full bg-white/10">
          <div
            className={`h-3 rounded-full transition-all duration-700 ${isQualiopi ? 'bg-success' : 'bg-accent'}`}
            style={{ width: `${percent}%` }}
          />
        </div>
        <div className="flex gap-3 mt-3">
          {RNQ.map((ind) => {
            const done = ind.criteria.filter((c) => items[c.id]?.isDone).length
            const pct = Math.round((done / ind.criteria.length) * 100)
            return (
              <div key={ind.id} className="flex-1 text-center">
                <div className="w-full h-1.5 rounded-full bg-white/10 mb-1">
                  <div className={`h-1.5 rounded-full ${pct === 100 ? 'bg-success' : 'bg-accent/60'}`} style={{ width: `${pct}%` }} />
                </div>
                <p className="text-[10px] text-text-muted">Ind. {ind.id}</p>
              </div>
            )
          })}
        </div>
      </div>

      {/* Indicateurs */}
      {loading ? (
        <div className="space-y-4">{[1, 2, 3].map((i) => <div key={i} className="glass-card h-32 animate-pulse" />)}</div>
      ) : (
        <div className="space-y-4">
          {RNQ.map((ind) => {
            const done = ind.criteria.filter((c) => items[c.id]?.isDone).length
            const isComplete = done === ind.criteria.length
            return (
              <div key={ind.id} className={`glass-card border ${isComplete ? 'border-success/20' : 'border-white/[0.08]'}`}>
                <div className="p-4 border-b border-white/[0.06]">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                        isComplete ? 'bg-success/20 text-success' : 'bg-white/[0.08] text-text-secondary'
                      }`}>
                        {isComplete ? '✓' : ind.id}
                      </div>
                      <div>
                        <p className={`font-semibold text-sm ${isComplete ? 'text-success' : 'text-text-primary'}`}>
                          Indicateur {ind.id} — {ind.label}
                        </p>
                        <p className="text-text-muted text-xs">{done}/{ind.criteria.length} critères</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="divide-y divide-white/[0.04]">
                  {ind.criteria.map((c) => {
                    const item = items[c.id]
                    const isDone = item?.isDone ?? false
                    const hasEvidence = !!item?.evidence
                    return (
                      <div key={c.id} className={`flex items-start gap-3 px-4 py-3 group transition-colors ${isDone ? 'bg-success/[0.03]' : 'hover:bg-white/[0.02]'}`}>
                        <button
                          onClick={() => toggle(ind.id, c.id)}
                          disabled={saving === c.id}
                          className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-all mt-0.5 ${
                            isDone ? 'border-success bg-success/20' : 'border-white/20 hover:border-white/40'
                          }`}
                        >
                          {saving === c.id ? (
                            <span className="w-3 h-3 border border-accent/30 border-t-accent rounded-full animate-spin" />
                          ) : isDone ? (
                            <span className="text-success text-xs">✓</span>
                          ) : null}
                        </button>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm leading-snug ${isDone ? 'text-text-secondary line-through opacity-70' : 'text-text-secondary'}`}>
                            <span className="text-text-muted font-mono text-xs mr-2">{c.id}</span>
                            {c.label}
                          </p>
                          {hasEvidence && (
                            <p className="text-xs text-text-muted mt-1 italic">
                              📎 {item.evidence}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => {
                            setOpenEvidence(c.id)
                            setEvidenceText(item?.evidence ?? '')
                          }}
                          title="Ajouter une preuve"
                          className="flex-shrink-0 p-1 rounded-lg hover:bg-white/[0.08] text-text-muted hover:text-text-secondary transition-colors opacity-0 group-hover:opacity-100"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                          </svg>
                        </button>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Modal preuve */}
      {openEvidence && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="glass-card p-6 w-full max-w-lg">
            <h2 className="text-sm font-semibold text-text-primary mb-1">Preuve — critère {openEvidence}</h2>
            <p className="text-text-muted text-xs mb-4">Décrivez comment vous couvrez ce critère (document, procédure, outil…)</p>
            <textarea
              value={evidenceText}
              onChange={(e) => setEvidenceText(e.target.value)}
              rows={4}
              maxLength={1000}
              placeholder="Ex : Questionnaire de satisfaction envoyé automatiquement 48h après chaque formation via Typeform (lien : …)"
              className="w-full bg-white/[0.05] border border-white/[0.12] rounded-xl px-4 py-3 text-text-primary text-sm placeholder:text-text-muted resize-none focus:outline-none focus:border-accent/50"
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={saveEvidence}
                disabled={saving === openEvidence}
                className="glass-btn-accent px-5 py-2.5 text-sm rounded-xl flex-1"
              >
                {saving === openEvidence ? 'Enregistrement…' : 'Enregistrer'}
              </button>
              <button onClick={() => setOpenEvidence(null)} className="glass-btn px-5 py-2.5 text-sm rounded-xl">
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
